/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client/react';

import { Button, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { Button as EventingV3Button } from '@coursera/event-pulse/react';

import { sendVerificationEmail as sendRecoveryVerificationEmail } from 'bundles/enterprise-legacy-learner-home/utils/RecoveryEmailAPIUtils';
import {
  SendVerificationEmailMutation,
  type SendVerificationEmailResult,
  type SendVerificationEmailVariables,
} from 'bundles/saml-sso/components/queries/SendVerificationEmailQuery';
import { allowUniqueRecoveryEmail } from 'bundles/saml-sso/epicFeatureFlags';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type ConfirmOrResendEmailStepProps = {
  emailField: string;
  userId: number;
  onUpdateEmailClick: () => void;
  onClose: () => void;
};

const styles = {
  subtitle: css`
    margin-top: var(--cds-spacing-400);
    display: inline-block;
  `,
  disclaimer: css`
    margin-top: var(--cds-spacing-200);
    display: inline-block;
  `,
  resendNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
  linkAction: css`
    padding: unset;
    margin: 0 var(--cds-spacing-50) var(--cds-spacing-50);

    &:hover,
    &:focus-within {
      background-color: var(--cds-color-neutral-background-primary) !important;
      text-decoration: none;
    }
  `,
  linkText: css`
    color: var(--cds-color-interactive-primary);
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  `,
  resendButton: css`
    margin-block-start: var(--cds-spacing-300);
    margin-inline-start: 0;
  `,
};

const { HeadingGroup, Content, Actions } = Dialog;

const ConfirmRecoveryEmailStep = ({
  emailField,
  userId,
  onUpdateEmailClick,
  onClose,
}: ConfirmOrResendEmailStepProps) => {
  const [showRetrySuccessful, setShowRetrySuccessful] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [sendVerificationEmail] = useMutation<SendVerificationEmailResult, SendVerificationEmailVariables>(
    SendVerificationEmailMutation
  );
  const thirdPartyOrgContext = useThirdPartyOrganization();

  const sendVerificationEmailUtil = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (recoveryEmail: string, isRetry = false) => {
      setShowRetrySuccessful(false);
      setShowError(false);

      if (allowUniqueRecoveryEmail({ orgId: thirdPartyOrgContext?.id ?? '' })) {
        return sendRecoveryVerificationEmail({ emailAddress: recoveryEmail, userId })
          .then(() => {
            if (isRetry) {
              setShowRetrySuccessful(true);
            }
          })
          .catch(() => {
            setShowError(true);
          });
      }

      return sendVerificationEmail({
        variables: {
          input: {
            emailAddress: recoveryEmail,
            userId,
          },
        },
        onCompleted: () => {
          if (isRetry) {
            setShowRetrySuccessful(true);
          }
        },
        onError: () => {
          setShowError(true);
        },
      });
    },
    [sendVerificationEmail, setShowRetrySuccessful, userId, thirdPartyOrgContext?.id]
  );

  React.useEffect(() => {
    sendVerificationEmailUtil(emailField);
  }, [sendVerificationEmailUtil, emailField]);

  const retrySendVerificationEmail = () => {
    sendVerificationEmailUtil(emailField, true);
  };

  return (
    <>
      <HeadingGroup id="title">{_t('Check your email')}</HeadingGroup>
      <Content>
        <Typography2 component="p" id="description">
          {thirdPartyOrgContext
            ? _t(
                'Check your inbox for a verification email sent to #{emailAddress}. Verify now to avoid losing access to your #{orgName} account in the future.',
                {
                  emailAddress: emailField,
                  orgName: thirdPartyOrgContext.name,
                }
              )
            : _t(
                'Check your inbox for a verification email sent to #{emailAddress}. Verify now to avoid losing access to your organization account in the future.',
                {
                  emailAddress: emailField,
                }
              )}
        </Typography2>
        <Typography2 css={styles.subtitle} component="p">
          {_t('Need to reenter your email? ')}
        </Typography2>
        <EventingV3Button
          variant="ghost"
          onClick={onUpdateEmailClick}
          css={styles.linkAction}
          trackingData={{
            button: { name: 'edit_recovery_email_button' as ButtonName },
            ...(thirdPartyOrgContext?.slug && { enterprise: { organizationSlug: thirdPartyOrgContext.slug } }),
          }}
        >
          <Typography2 component="span" css={styles.linkText}>
            {_t('Update your email address')}
          </Typography2>
        </EventingV3Button>

        <Button variant="ghost" css={[styles.linkAction, styles.resendButton]} onClick={retrySendVerificationEmail}>
          <Typography2 component="span" css={styles.linkText}>
            {_t('Resend verification email')}
          </Typography2>
        </Button>

        <div role="region" aria-live="polite">
          {showRetrySuccessful && (
            <InlineNotification css={styles.resendNotification} severity="success">
              {_t('We sent you a new verification email')}
            </InlineNotification>
          )}
          {showError && (
            <InlineNotification css={styles.resendNotification} severity="error">
              {_t('We couldn’t resend the verification email. Please try again.')}
            </InlineNotification>
          )}
        </div>
      </Content>
      <Actions>
        <Button variant="primary" onClick={onClose} size="medium">
          {_t('Done')}
        </Button>
      </Actions>
    </>
  );
};

export default ConfirmRecoveryEmailStep;
